/**
 * @file WmsWriteoffsPage.tsx
 * @description Реестр списаний (phase13.0; фильтры — phase19.23, A2;
 * phase19.31 зона «Склад» — переведён целиком на Commora).
 *
 * Заголовок «Списания» в верхней панели, главное действие одно — «+ Списание»
 * обычной градиентной кнопкой: красный цвет — для опасного действия, а
 * завести черновик не опасно (правило «цвет = смысл»). На телефоне и
 * планшете действие — в общей нижней полосе каркаса. Фильтры: поиск, статус
 * пилюлями, склад, причина. Список — `ResponsiveList`, номер — в одну строку.
 *
 * Поиск, статус, склад, причина и страницы считает сервер
 * (`useWmsDocumentsList`).
 */

import { Plus, Search, Trash2 } from "lucide-react";
import { Link, useParams } from "react-router";

import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { FilterBar } from "../../../components/ui/FilterBar";
import { FilterChips } from "../../../components/ui/FilterChips";
import { SearchInput } from "../../../components/ui/SearchInput";
import { SelectField } from "../../../components/ui/SelectField";
import { WRITEOFF_COLUMNS } from "../../../components/wms/registry/writeoff-columns";
import { WmsDocumentsListBody } from "../../../components/wms/registry/WmsDocumentsListBody";
import { useWmsDocumentsList } from "../../../components/wms/useWmsDocumentsList";
import { WRITEOFF_REASON_LABEL } from "../../../lib/api-wms";
import { warehouseOptions } from "../../../lib/wms-registry-filters";

/** Статусы списания — пилюлями; новый статус = запись. */
const STATUS_CHIPS: { value: "" | "DRAFT" | "COMMITTED"; label: string }[] = [
  { value: "", label: "Все" },
  { value: "DRAFT", label: "Черновики" },
  { value: "COMMITTED", label: "Проведённые" },
];

const REASON_OPTIONS = [
  { value: "", label: "Все причины" },
  ...Object.entries(WRITEOFF_REASON_LABEL).map(([value, label]) => ({ value, label })),
];

export function WmsWriteoffsPage() {
  const { slug } = useParams<{ slug: string }>();
  const basePath = `/shop/${slug}/wms/writeoffs`;
  const list = useWmsDocumentsList("writeoffs", "Не удалось загрузить списания. Проверьте связь и попробуйте ещё раз.");
  const createLink = (
    <Button asChild variant="brand">
      <Link to={`${basePath}/new`}>
        <Plus className="size-4" />
        Списание
      </Link>
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Списания"
        subtitle="Брак, просрочка, потеря — остаток снимается с партии"
        action={createLink}
      />

      <FilterBar
        search={
          <SearchInput
            value={list.search}
            onChange={list.setSearch}
            placeholder="Номер, склад или сотрудник"
            ariaLabel="Поиск по списаниям"
          />
        }
        // Телефон: статус — в шторке «Фильтры · N» (единое решение склада).
        chipsCollapse={{ activeCount: list.status ? 1 : 0, onReset: () => list.setStatus("") }}
        chips={
          <FilterChips
            aria-label="Статус списания"
            items={STATUS_CHIPS}
            value={list.status as (typeof STATUS_CHIPS)[number]["value"]}
            onChange={list.setStatus}
          />
        }
      >
        <SelectField
          value={list.warehouseId}
          onChange={list.setWarehouseId}
          options={warehouseOptions(list.warehouses)}
          placeholder="Все склады"
          ariaLabel="Склад"
          className="w-full sm:w-[200px]"
        />
        <SelectField
          value={list.reason}
          onChange={list.setReason}
          options={REASON_OPTIONS}
          placeholder="Все причины"
          ariaLabel="Причина"
          className="w-full sm:w-[200px]"
        />
      </FilterBar>

      <WmsDocumentsListBody
        list={list}
        columns={WRITEOFF_COLUMNS}
        rowKey={(w) => w.id}
        rowHref={(w) => `${basePath}/${w.id}`}
        rowLabel={(w) => `Списание ${w.writeoffNumber}`}
        ariaLabel="Реестр списаний"
        filtered={list.filtering}
        empty={{
          icon: Trash2,
          title: "Списаний пока нет",
          description: "Списание снимает остаток с партии: брак, просрочка, потеря.",
        }}
        filteredEmpty={{
          icon: Search,
          title: "Под эти условия списаний нет",
          description: "Измените поиск, статус, склад или причину.",
          onReset: list.reset,
        }}
        allShownText="Показаны все списания"
      />
    </div>
  );
}
